import {DateValue, parseAbsoluteToLocal} from '@internationalized/date';
import React, {memo} from 'react';
import {useDateFormatter} from '@ui/i18n/use-date-formatter';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {useSettings} from '@ui/settings/use-settings';
import {useUserTimezone} from '@ui/i18n/use-user-timezone';
import {formatDistanceToNow} from 'date-fns';
import {fr, enUS} from 'date-fns/locale';
import {AccessTimeFilledIcon} from '@ui/icons/material/AccessTimeFilled';
import {Title} from '@app/titles/models/title';

import {useSelectedLocale} from '@ui/i18n/selected-locale';

export const DateFormatPresets: Record<
  'numeric' | 'short' | 'long' | 'timestamp',
  Intl.DateTimeFormatOptions
> = {
  numeric: {year: 'numeric', month: '2-digit', day: '2-digit'},
  short: {year: 'numeric', month: 'short', day: '2-digit'},
  long: {month: 'long', day: '2-digit', year: 'numeric'},
  timestamp: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  },
};

interface FormattedDateProps {
  date?: string | DateValue | Date;
  options?: Intl.DateTimeFormatOptions;
  preset?: keyof typeof DateFormatPresets;
  item?: Title;
}

export const FormattedDateLink = memo(
  ({date, options, preset, item}: FormattedDateProps) => {
    const {dates} = useSettings();
    const timezone = useUserTimezone();
    const {locale} = useSelectedLocale();
    const formatter = useDateFormatter(
      options ||
        (DateFormatPresets as Record<string, Intl.DateTimeFormatOptions>)[
          preset || dates?.format
        ],
    );

    if (!date) {
      return null;
    }

    // make sure date with invalid format does not blow up the app
    try {
      if (typeof date === 'string') {
        date = parseAbsoluteToLocal(date).toDate();
      } else if ('toDate' in date) {
        date = date.toDate(timezone);
      }
    } catch (e) {
      return null;
    }
    const delay = 10;
    return (
      <div className="text-gray-300 me-2 rounded bg-[#252529] px-4  py-2 text-xs font-medium">
        <AccessTimeFilledIcon size="20" />{' '}
        {getTimeAgoShort(date as Date, locale.language)}
      </div>
    );
  },
  shallowEqual,
);

const getTimeAgoShort = (date: string | number | Date, langue: string) => {
  const distance = formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: langue === 'en' ? enUS : fr,
  });
  if (langue === 'en') {
    return distance.replace(/almost/, '').replace(/ ago/, '');
  } else {
    return distance.replace(/il y a/, '').replace(/environ/, '');
  }
};
const getTimeAgo = (date: string | number | Date, langue: string) => {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: langue === 'en' ? enUS : fr,
  });
};
